import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { invalidateSkillCollectionListQueries } from "./cache-utils";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { ToastType } from "@/enums";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";
import { displayErrorToast, displayToast } from "@/utils/toast";

interface CreateSkillCollectionParams {
  workspaceId: number;
  name: string;
  description?: string | null;
  folderId?: number | null;
  provider?: string | null;
  files?: {
    path: string;
    content: string;
  }[];
  commitMessage?: string;
}

interface CreateSkillCollectionResponse {
  success: boolean;
  skill_collection: {
    id: string;
    workspace_id: number;
    folder_id: number | null;
    root_path: string;
    name: string;
    description: string | null;
    provider: string | null;
    is_deleted: boolean;
    created_by: number | null;
    created_at: string;
    updated_by: number | null;
    updated_at: string;
  };
}

interface UseCreateSkillCollectionOptions {
  onSuccess?: (data: CreateSkillCollectionResponse, variables: CreateSkillCollectionParams) => void;
}

export const useCreateSkillCollection = (options?: UseCreateSkillCollectionOptions) => {
  const auth = useAuth();
  const userToken = auth?.userToken ?? "";
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async ({
      workspaceId,
      name,
      description,
      folderId,
      provider,
      files: filesParam,
      commitMessage,
    }: CreateSkillCollectionParams): Promise<CreateSkillCollectionResponse> => {
      const res = await fetch(ENDPOINTS.skill_collections.list(workspaceId), {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({
          name,
          description: description ?? null,
          folder_id: folderId ?? null,
          provider: provider ?? null,
          files: filesParam ?? [],
          commit_message: commitMessage,
        }),
      });
      return handleResponse(res);
    },
    onSuccess: (data, variables) => {
      invalidateSkillCollectionListQueries(queryClient, variables.workspaceId);
      displayToast("Skill collection created", ToastType.success);
      if (options?.onSuccess) {
        options.onSuccess(data, variables);
      } else {
        navigate(
          `/workspace/${variables.workspaceId}/skill-collection/${data.skill_collection.id}`,
        );
      }
    },
    onError: (error: Error) => {
      displayErrorToast(error.message);
    },
  });
};
